import React, { useEffect, useState } from 'react';
import type {
  BookingErrorKey,
  BookingErrors,
  BookingFields,
  BookingRequest,
  BookingStatus,
} from '../types/booking';
import type { Lang } from '../types/copy';

const ERROR_ORDER: BookingErrorKey[] = ['name', 'email', 'phone', 'service'];

export function useBookingForm(request: BookingRequest | null, siteLang: Lang) {
  const [fields, setFields] = useState<BookingFields>(() => emptyFields(siteLang, request));
  const [errors, setErrors] = useState<BookingErrors>({});
  const [status, setStatus] = useState<BookingStatus>('idle');

  useEffect(() => {
    if (!request) return;
    setFields((f) => ({ ...f, service: request.id }));
    setErrors((e) => ({ ...e, service: undefined }));
    setStatus('idle');
  }, [request]);

  function update<K extends keyof BookingFields>(key: K, value: BookingFields[K]) {
    setFields((f) => ({ ...f, [key]: value }));
    if (key in errors) {
      setErrors((e) => ({ ...e, [key]: undefined }));
    }
  }

  function submit(event: React.FormEvent, onValid: (fields: BookingFields) => void) {
    event.preventDefault();
    const next = validate(fields);
    setErrors(next);
    const firstInvalid = ERROR_ORDER.find((k) => next[k]);
    if (firstInvalid) {
      document.getElementById(`booking-${firstInvalid}`)?.focus();
      return;
    }
    onValid(fields);
    setStatus('success');
  }

  function reset() {
    setFields(emptyFields(siteLang, null));
    setErrors({});
    setStatus('idle');
  }

  return { fields, errors, status, update, submit, reset };
}

function emptyFields(lang: Lang, request: BookingRequest | null): BookingFields {
  return {
    name: '',
    email: '',
    phone: '',
    service: request?.id ?? '',
    schedule: 'flexible',
    language: lang,
    message: '',
  };
}

function validate(f: BookingFields): BookingErrors {
  const e: BookingErrors = {};
  if (!f.name.trim()) e.name = true;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = true;
  if (f.phone.replace(/\D/g, '').length < 7) e.phone = true;
  if (!f.service) e.service = true;
  return e;
}
